import {Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ApiClientService} from '../../../../services/api-client.service';
import {DocumentsService} from '../../../../services/documents.service';
import {UrlService} from '../../../../services/url.service';
import {ShareType} from '../../../../types/documents/share-types';
import {NgForm} from '@angular/forms';
import {untilDestroyed} from 'ngx-take-until-destroy';

declare const toastr: any;

@Component({
    selector: 'file-details-step',
    templateUrl: './file-details-step.component.html',
    styleUrls: ['./file-details-step.component.scss']
})
export class FileDetailsStepComponent implements OnInit, OnDestroy {
    private dayInMilliseconds = 1000 * 60 * 60 * 24;
    @Input() submitText: string = 'Save';
    @Input() isShowPreloader: boolean = false;
    @Output() notify = new EventEmitter();
    @Output() nextStep = new EventEmitter();

    @HostBinding('class.sk-loading')
    get isLoading() {
        return this.isPermissionsLoading || this.isShowPreloader;
    }

    data: IFile = new IFile();
    permissionsList: Array<any> = [];
    corporationNumbers: Array<any> = [];
    buildings: Array<any> = [];
    fileNames: Array<string> = [];
    isPermissionsLoading: boolean = false;
    isCorporationLoading: boolean = false;
    isShared = false;

    shareTypes = ShareType;

    shareTypeOptions = Object.keys(ShareType).map(key => ({name: key, value: ShareType[key]}));
    haveaParent = false;

    @ViewChild('fileInput') fileInput: ElementRef;
    currentShareTypeOptions: Array<any> = this.shareTypeOptions;
    private userGroups: Array<any> = [];
    private parentPermissions = [];
    private shareTypeValue: ShareType = null;
    private allowedFileExtensions = new Set<string>([
        'pdf',
        // office files
        'doc', 'dot', 'wbk', 'docx', 'docm', 'dotx', 'dotm', 'docb', 'xls', 'xlt', 'xlm', 'xlsx', 'xlsm',
        'xltx', 'xltm', 'xlsb', 'xla', 'xlam', 'xll', 'xlw', 'ppt', 'pot', 'pps', 'pptx', 'pptm', 'potx',
        'potm', 'ppam', 'ppsx', 'ppsm', 'sldx', 'sldm', 'accdb', 'accde', 'accdt', 'accdr', 'one', 'pub', 'xps',

        // image files
        'jpg', 'jpeg', 'jpe', 'jif', 'jfif', 'jfi', 'png', 'gif', 'webp', 'tiff', 'tif', 'psd', 'raw',
        'arw', 'cr2', 'nrw', 'k25', 'bmp', 'dib', 'heif', 'heic', 'ind', 'indd', 'indt', 'jp2',
        'j2k', 'jpf', 'jpx', 'jpm', 'mj2', 'svg', 'svgz', 'ai', 'eps'
    ]);
    hasAcceptableDates: boolean;
    private _visibleFrom: Date;
    private _visibleTo: any;

    constructor(public api: ApiClientService,
                public ds: DocumentsService,
                public urlService: UrlService,
    ) {
    }


    get visibleFrom(): Date {
        return this._visibleFrom;
    }

    set visibleFrom(value: Date) {
        this._visibleFrom = value;
        if (this._visibleTo) {
            if (this._visibleFrom > this._visibleTo) {
                const visibleFrom = new Date(this._visibleFrom.valueOf()).getTime();
                this._visibleTo = new Date(visibleFrom + this.dayInMilliseconds);
            }
        }
    }

    get visibleTo(): any {
        return this._visibleTo;
    }

    set visibleTo(value: any) {
        this._visibleTo = value;
        if (this._visibleFrom) {
            if (this._visibleFrom > this._visibleTo) {
                const visibleTo = new Date(this._visibleTo.valueOf()).getTime();
                this._visibleFrom = new Date(visibleTo - this.dayInMilliseconds);
            }
        }
    }

    ngOnInit() {
        this.ds.corporationsListLoaded
            .pipe(untilDestroyed(this))
            .subscribe((data) => {
                if (data)
                    this.getCorporationNumberList();
                else
                    this.isCorporationLoading = true;
            });

        this.loadPermissions();

        this.data.notifyUsers = false;
        this.fileInput.nativeElement.click();
        this.urlService.modalOpened.emit();
    }

    private loadPermissions(flag?): void {
        this.isPermissionsLoading = true;
        this.ds.getParentPermissions().subscribe(({
                                                      permissionsList,
                                                      parentPermission,
                                                      documentsPermissions,
                                                      userGroups,
                                                      isPublic
                                                  }) => {
            this.isPermissionsLoading = false;
            this.permissionsList = permissionsList.map(this.ds.parsePermission);
            this.parentPermissions = parentPermission.map(this.ds.parsePermission);

            if (documentsPermissions !== null || userGroups !== null || isPublic !== null) {
                if (documentsPermissions !== null && documentsPermissions.length) {
                    this.data.permissions = documentsPermissions.map(this.ds.parsePermission);
                }
                if (userGroups !== null) {
                    this.userGroups = userGroups.map(role => ({
                        id: role.bdId,
                        itemName: role.name
                    }));
                    this.data.userGroups = JSON.parse(JSON.stringify(this.userGroups));
                }

                this.initSareValues({documentsPermissions, userGroups, isPublic});
            } else {
                this.userGroups = JSON.parse(JSON.stringify(this.ds.userGroups));
            }

            if (flag) {
                this.isShared = flag;
            }
        });
    }

    initSareValues(parentPermissions) {
        if (parentPermissions.isPublic) {
            this.shareTypeValue = ShareType.Public;
        } else if (parentPermissions.documentsPermissions.length) {
            this.shareTypeValue = ShareType.Residents;
        } else if (parentPermissions.userGroups.length) {
            this.shareTypeValue = ShareType.Groups;
        } else {
            this.shareTypeValue = null;
        }
        this.isShared = this.shareTypeValue !== null;
        this.currentShareTypeOptions = this.shareTypeOptions.filter(item => item.value == this.shareTypeValue);
    }

    set shareType(value: ShareType) {
        this.shareTypeValue = value;
        this.isShared = value !== null;

        this.data.isPublic = this.shareTypeValue === ShareType.Public;

        if (this.shareTypeValue !== ShareType.Residents) {
            this.data.permissions = []; // force reset permissions
        } else if (!this.data.permissions.length) {
            this.data.permissions = [...this.parentPermissions]; // set parent permissions as initial state
        }

        if (this.shareTypeValue !== ShareType.Groups) {
            this.data.userGroups = []; // force reset group sharing
        }
    }

    get shareType(): ShareType {
        return this.shareTypeValue;
    }

    onFileChange(e) {
        this.data.files = [];
        this.fileNames = [];
        for (const file of e.target.files) {
            if (file.size >= 25000000) {
                toastr.warning(`${file.name} was not uploaded,because max file size 25 MB.`);
                continue;
            }
            const nameSplit = file.name.split('.');
            const extension = nameSplit[nameSplit.length - 1].toLowerCase();
            if (!this.allowedFileExtensions.has(extension)) {
                toastr.warning(`${file.name} was not uploaded, because file's extension is not allowed`);
                continue;
            }

            this.data.files.push(file);
            this.fileNames.push(file.name);
        }
        e.target.value = null;
    }

    onSubmit(form: NgForm) {
        let isInvalid = false;

        if (this.shareType === ShareType.Groups && !(this.data.userGroups && this.data.userGroups.length)) {
            toastr.warning('Group should not be blank.');
            isInvalid = true;
        }

        if (this.buildings.length === 1) { // had to
            this.data.buildings = [...this.buildings];
        }

        if (!this.data.corporationNumbers.length) {
            toastr.warning('Corporation Numbers should not be blank.');
            isInvalid = true;
        }

        if (!this.data.buildings.length) {
            toastr.warning('Buildings should not be blank.');
            isInvalid = true;
        }

        if (!this.data.files.length) {
            toastr.warning('Files should not be blank.');
            isInvalid = true;
        }

        if (this.shareType === ShareType.Residents && !(this.data.permissions && this.data.permissions.length)) {
            toastr.warning('Access should not be blank.');
            isInvalid = true;
        }

        if (isInvalid) {
            return;
        }

        const files = [];
        const acceptableDates = {};
        if (this.hasAcceptableDates) {
            if (this.visibleFrom) {
                const acceptableFrom = new Date(this.visibleFrom.valueOf());
                acceptableFrom.setHours(0);
                acceptableFrom.setMinutes(0);
                acceptableFrom.setSeconds(0);
                acceptableDates['acceptableFrom'] = acceptableFrom.toISOString();
            }
            if (this.visibleTo) {
                const acceptableTo = new Date(this.visibleTo.valueOf());
                acceptableTo.setHours(23);
                acceptableTo.setMinutes(59);
                acceptableTo.setSeconds(59);
                acceptableDates['acceptableTo'] = acceptableTo.toISOString();
            }
        }
        const permissionsAndUserGroups = {
            permissions: this.data.permissions.map((item) => ({permission: item.id})),
            userGroups: this.data.userGroups.map(g => ({
                bdId: g.id,
                name: g.itemName
            }))
        };
        this.data.files.forEach((file, index) => {
            const data = {
                corporationNumbers: this.data.corporationNumbers,
                buildings: this.data.buildings,
                file: file,
                name: this.fileNames[index],
                isPublic: this.data.isPublic,
                notify: this.data.notifyUsers,
                ...acceptableDates,
                ...permissionsAndUserGroups
            };
            files.push(data);
        });

        this.nextStep.emit(files);
    }

    getBuildingsForSelectedCorporations(event?, eventType?) {
        const buildings = [];

        if (!this.haveaParent) {
            this.data.corporationNumbers.forEach(corp => {
                this.ds.buildingsForUpdate.forEach(building => {
                    if ((<any>building).corporationNumber == corp.id)
                        buildings.push({
                            ...building,
                            itemName: building.name,
                        });
                });
            });
            this.buildings = buildings;
        } else {
            this.getParentBuildings();
        }

        if (eventType == 'deselect')
            this.data.buildings = this.data.buildings.filter(b => b.corporationNumber != event.id);
        else if (eventType == 'deselectAll')
            this.data.buildings = [];

        if (this.buildings.length === 1) {
            this.data.buildings = [...this.buildings];
        }
    }

    getParentBuildings() {
        const parentBuildings = this.ds.getParentBuildings();

        if (!parentBuildings.length) return;

        this.buildings = [];
        this.data.buildings = [];

        if (!this.data.corporationNumbers.length) return;

        this.buildings = parentBuildings.map(building => ({
            ...building,
            itemName: building.name,
        }));
    }

    setDataBuildings() {
        this.data.buildings = JSON.parse(JSON.stringify(this.buildings));
        this.loadPermissions(true);
    }

    getCorporationNumberList() {
        this.corporationNumbers = [];
        const availableCorporationNumbers = this.ds.buildingsForUpdate.reduce((acc, next) => {
            acc[next.corporationNumber] = true;
            return acc;
        }, {});
        this.corporationNumbers = this.ds.corporations
            .filter(corporation => availableCorporationNumbers.hasOwnProperty(corporation.id))
            .map(item => ({
                ...item,
                itemName: item.name,
                disabled: item.disabled
            }));
        const parentCorporations = this.ds.getParentCorporations(this.corporationNumbers);
        this.data.corporationNumbers = parentCorporations && parentCorporations.length > 0 ? parentCorporations : [];
        if ((!parentCorporations || parentCorporations.length < 1) && this.corporationNumbers.length === 1) {
            this.data.corporationNumbers = JSON.parse(JSON.stringify(this.corporationNumbers));
            this.getBuildingsForSelectedCorporations();
        }
        if (parentCorporations.length) {
            this.haveaParent = true;
            this.corporationNumbers = this.corporationNumbers.filter(item => {
                return parentCorporations.some(element => String(item.id) === String(element.id));
            });
            this.getParentBuildings();
            this.setDataBuildings();
        }
        this.isCorporationLoading = false;
    }

    trackByFn(index, item) {
        return index; // or item.id
    }

    removeFile(index) {
        this.data.files.splice(index, 1);
        this.fileNames.splice(index, 1);
    }

    getFilesNamesString() {
        let string = '';
        this.data.files.forEach((file, index) => {
            string += file.name;
            if (index < this.data.files.length - 1)
                string += ', ';
        });
        return string;

    }

    ngOnDestroy() {
    }

    notifyUsersValueChange(notify: boolean) {
        this.notify.emit(notify);
    }

    shareTypeChange(selectedShareType: string) {
        this.hasAcceptableDates = selectedShareType !== null;
    }
}

class IFile {
    corporationNumbers: Array<any> = [];
    buildings: Array<any> = [];
    files: Array<File> = [];
    permissions: Array<any> = [];
    userGroups: Array<any> = [];
    isPublic: boolean;
    notifyUsers: boolean;
}



// WEBPACK FOOTER //
// /app/src/app/views/documents/upload-file-modal/file-details-step/file-details-step.component.ts